/*
 * @providesModule DepositSuccessPopup
 */

import {StyleSheet, Animated, View, TouchableOpacity, Platform} from 'react-native';
import React, {useRef, useEffect, useState} from 'react';
import DepositAmount from './Components/DepositAmount';
import AdditionalRewards from './Components/AdditionalRewards/AdditionalRewards';
import RecommendedTemplate from './Components/RecommendedTemplate';
import {DepositSuccessModalDeposit} from 'DepositSuccessModal';
import SkillzImage from 'SkillzImage';
import ClientPropertyConstants from 'ClientPropertyConstants';

type DepositSuccessPopupProps = {
  duration?: number;
  onDismiss: () => void;
  deposit: DepositSuccessModalDeposit;
  onCancelPress?: () => void;
};

const DepositSuccessPopup = ({
  duration = 500,
  onDismiss,
  deposit,
  onCancelPress,
}: DepositSuccessPopupProps) => {
  const componentMountTime = useRef(Date.now());
  const isIOS = Platform.OS === 'ios';
  const fadeAnim = useRef(new Animated.Value(0)).current;
  const fadeAmim2 = useRef(new Animated.Value(0)).current;
  const [isDismissed, setIsDismissed] = useState(false);
  const closeButtonAnimationStarted = useRef(false);
  const closeButtonAnimationCompleted = useRef(false);
  const [fadeAnimValue, setFadeAnimValue] = useState(0);
  const [fadeAmim2Value, setFadeAmim2Value] = useState(0);

  console.debug('DepositSuccessPopup: Component rendering', {
    platform: Platform.OS,
    duration,
    deposit: {
      depositAmount: deposit.depositAmount,
      bonusCash: deposit.bonusCash,
      promoCash: deposit.promoCash,
      ticketz: deposit.ticketz,
      points: deposit.points,
    },
    timestamp: componentMountTime.current,
  });

  const isDepositSuccessEnabled = ClientPropertyConstants.getStoreV2DepositSuccessEnabled();
  if (!isDepositSuccessEnabled) {
    console.debug('DepositSuccessPopup: Feature disabled, returning null');
    return null;
  }

  const depositOnlyBonusCash = deposit.bonusCash > 0 && !deposit.ticketz && !deposit.points;
  const depositBonusCashAndNonCashRewards =
    deposit.bonusCash > 0 && (deposit.ticketz || deposit.points);
  const depositOnlyNonCashRewards = deposit.bonusCash === 0 && (deposit.ticketz || deposit.points);
  const depositNoRewards = deposit.bonusCash === 0 && !deposit.ticketz && !deposit.points;

  console.debug('DepositSuccessPopup: Deposit type classification', {
    depositOnlyBonusCash,
    depositBonusCashAndNonCashRewards,
    depositOnlyNonCashRewards,
    depositNoRewards,
  });

  // Recommended template delay depends on the type of deposit
  const getRecommendedTemplateDelay = () => {
    let delay = 2000;
    if (depositNoRewards) {
      delay = 2000;
    } else if (depositOnlyBonusCash || depositOnlyNonCashRewards) {
      delay = isIOS ? 5000 : 5700;
    } else if (depositBonusCashAndNonCashRewards) {
      delay = isIOS ? 7000 : 7500;
    }

    console.debug('DepositSuccessPopup: getRecommendedTemplateDelay calculated', {
      delay,
      platform: Platform.OS,
      depositType: depositNoRewards
        ? 'noRewards'
        : depositOnlyBonusCash || depositOnlyNonCashRewards
          ? 'singleReward'
          : 'multipleRewards',
    });
    return delay;
  };
  // Carful to tweak these values, minor changes can cause issues with animation timing/overlap
  const animationDelay = {
    depositAmount: 1500,
    additionalRewards: 2000,
    recommendedTemplate: getRecommendedTemplateDelay(),
    closeIcon: getRecommendedTemplateDelay(), // should be the same as recommended template delay
  };

  console.debug('DepositSuccessPopup: Animation delays calculated', {
    ...animationDelay,
    totalTimeUntilCloseButton: animationDelay.closeIcon + duration,
    timeUntilCloseButtonSeconds: (animationDelay.closeIcon + duration) / 1000,
  });

  // Track animation values
  useEffect(() => {
    const fadeAnimListener = fadeAnim.addListener(({value}) => {
      setFadeAnimValue(value);
    });
    const fadeAmim2Listener = fadeAmim2.addListener(({value}) => {
      setFadeAmim2Value(value);
    });

    return () => {
      fadeAnim.removeListener(fadeAnimListener);
      fadeAmim2.removeListener(fadeAmim2Listener);
    };
  }, []);

  useEffect(() => {
    const mountTime = Date.now();
    const timeSinceMount = mountTime - componentMountTime.current;
    console.debug('DepositSuccessPopup: useEffect triggered', {
      timeSinceMount,
      fadeAnimInitialValue: fadeAnimValue,
      fadeAmim2InitialValue: fadeAmim2Value,
    });

    // Start main container fade-in animation
    const mainAnimationStartTime = Date.now();
    console.debug('DepositSuccessPopup: Starting main container fade-in animation', {
      duration,
      startTime: mainAnimationStartTime,
    });

    const mainAnimation = Animated.timing(fadeAnim, {
      toValue: 1, // Final opacity
      duration, // Duration in ms
      useNativeDriver: true, // Use native driver for better perf
    });

    mainAnimation.start((result) => {
      const animationEndTime = Date.now();
      const actualDuration = animationEndTime - mainAnimationStartTime;
      console.debug('DepositSuccessPopup: Main container fade-in animation completed', {
        finished: result.finished,
        expectedDuration: duration,
        actualDuration,
        endTime: animationEndTime,
        timeSinceMount: animationEndTime - componentMountTime.current,
      });
    });

    // Start close button fade-in animation with delay
    const closeButtonDelay = animationDelay.closeIcon;
    const closeButtonExpectedStartTime = mountTime + closeButtonDelay;
    const closeButtonExpectedEndTime = closeButtonExpectedStartTime + duration;

    console.debug('DepositSuccessPopup: Scheduling close button fade-in animation', {
      delay: closeButtonDelay,
      delaySeconds: closeButtonDelay / 1000,
      duration,
      expectedStartTime: closeButtonExpectedStartTime,
      expectedEndTime: closeButtonExpectedEndTime,
      expectedEndTimeSeconds: closeButtonExpectedEndTime / 1000,
      timeUntilStart: closeButtonDelay,
      timeUntilStartSeconds: closeButtonDelay / 1000,
    });

    const closeButtonTimeout = setTimeout(() => {
      const actualStartTime = Date.now();
      const timeSinceMountAtStart = actualStartTime - componentMountTime.current;
      closeButtonAnimationStarted.current = true;

      console.debug('DepositSuccessPopup: Close button animation starting NOW', {
        expectedStartTime: closeButtonExpectedStartTime,
        actualStartTime,
        timeSinceMount: timeSinceMountAtStart,
        expectedDelay: closeButtonDelay,
        actualDelay: actualStartTime - mountTime,
        delayDifference: actualStartTime - closeButtonExpectedStartTime,
      });

      const closeButtonAnimation = Animated.timing(fadeAmim2, {
        toValue: 1,
        duration,
        useNativeDriver: true,
      });

      closeButtonAnimation.start((result) => {
        const animationEndTime = Date.now();
        const actualDuration = animationEndTime - actualStartTime;
        closeButtonAnimationCompleted.current = true;

        console.debug('DepositSuccessPopup: Close button fade-in animation completed', {
          finished: result.finished,
          expectedDuration: duration,
          actualDuration,
          startTime: actualStartTime,
          endTime: animationEndTime,
          timeSinceMount: animationEndTime - componentMountTime.current,
          expectedEndTime: closeButtonExpectedEndTime,
          endTimeDifference: animationEndTime - closeButtonExpectedEndTime,
          fadeAmim2FinalValue: fadeAmim2Value,
        });

        if (!result.finished) {
          console.warn('DepositSuccessPopup: Close button animation did NOT finish!', {
            fadeAmim2Value: fadeAmim2Value,
            timeSinceMount: animationEndTime - componentMountTime.current,
          });
        }
      });
    }, closeButtonDelay);

    return () => {
      console.debug(
        'DepositSuccessPopup: useEffect cleanup - component unmounting or dependencies changed',
        {
          closeButtonAnimationStarted: closeButtonAnimationStarted.current,
          closeButtonAnimationCompleted: closeButtonAnimationCompleted.current,
          timeSinceMount: Date.now() - componentMountTime.current,
        }
      );
      clearTimeout(closeButtonTimeout);
      mainAnimation.stop();
      fadeAmim2.stopAnimation();
    };
  }, [fadeAnim, fadeAmim2, duration]);

  useEffect(() => {
    console.debug('DepositSuccessPopup: Component mounted');
    return () => {
      const unmountTime = Date.now();
      const totalLifetime = unmountTime - componentMountTime.current;
      console.debug('DepositSuccessPopup: Component unmounting', {
        totalLifetime,
        totalLifetimeSeconds: totalLifetime / 1000,
        isDismissed,
        closeButtonAnimationStarted: closeButtonAnimationStarted.current,
        closeButtonAnimationCompleted: closeButtonAnimationCompleted.current,
        fadeAnimValue: fadeAnimValue,
        fadeAmim2Value: fadeAmim2Value,
      });
    };
  }, []);

  if (isDismissed) {
    console.debug('DepositSuccessPopup: Component dismissed, returning null');
    return null;
  }

  const handleDismiss = () => {
    const dismissTime = Date.now();
    const timeSinceMount = dismissTime - componentMountTime.current;
    console.debug('DepositSuccessPopup: handleDismiss called', {
      timeSinceMount,
      timeSinceMountSeconds: timeSinceMount / 1000,
      closeButtonAnimationStarted: closeButtonAnimationStarted.current,
      closeButtonAnimationCompleted: closeButtonAnimationCompleted.current,
      fadeAmim2Value: fadeAmim2Value,
      expectedCloseButtonTime: animationDelay.closeIcon + duration,
      closeButtonShouldBeVisible: timeSinceMount >= animationDelay.closeIcon + duration,
    });

    setIsDismissed(true);
    onDismiss();
  };

  const renderTime = Date.now();
  const timeSinceMount = renderTime - componentMountTime.current;
  const closeButtonOpacity = fadeAmim2Value;
  const closeButtonShouldBeVisible = timeSinceMount >= animationDelay.closeIcon;

  // Log render state periodically (every 2 seconds) to track if component is stuck
  useEffect(() => {
    const intervalId = setInterval(() => {
      const currentTime = Date.now();
      const elapsed = currentTime - componentMountTime.current;
      const closeButtonExpectedVisible = elapsed >= animationDelay.closeIcon + duration;

      console.debug('DepositSuccessPopup: Periodic render check', {
        elapsed,
        elapsedSeconds: elapsed / 1000,
        closeButtonOpacity: fadeAmim2Value,
        closeButtonExpectedVisible,
        closeButtonAnimationStarted: closeButtonAnimationStarted.current,
        closeButtonAnimationCompleted: closeButtonAnimationCompleted.current,
        isDismissed,
        fadeAnimValue: fadeAnimValue,
        fadeAmim2Value: fadeAmim2Value,
        expectedCloseButtonTime: animationDelay.closeIcon + duration,
        timeUntilCloseButton: Math.max(0, animationDelay.closeIcon + duration - elapsed),
      });

      // Warn if close button should be visible but isn't
      if (closeButtonExpectedVisible && fadeAmim2Value < 0.5) {
        console.warn('DepositSuccessPopup: Close button should be visible but opacity is low!', {
          elapsed,
          expectedOpacity: 1,
          actualOpacity: fadeAmim2Value,
          closeButtonAnimationStarted: closeButtonAnimationStarted.current,
          closeButtonAnimationCompleted: closeButtonAnimationCompleted.current,
        });
      }
    }, 2000);

    return () => clearInterval(intervalId);
  }, [animationDelay.closeIcon, duration]);

  console.debug('DepositSuccessPopup: Rendering component', {
    timeSinceMount,
    timeSinceMountSeconds: timeSinceMount / 1000,
    closeButtonOpacity,
    closeButtonShouldBeVisible,
    closeButtonAnimationStarted: closeButtonAnimationStarted.current,
    closeButtonAnimationCompleted: closeButtonAnimationCompleted.current,
    fadeAnimValue: fadeAnimValue,
    fadeAmim2Value: fadeAmim2Value,
    hasRewards: !depositNoRewards,
  });

  const handleCrossButton = () => {
    setIsDismissed(true);
    onDismiss();
    
    onCancelPress && onCancelPress();
  };

  return (
    <Animated.View style={[styles.container, {opacity: fadeAnim}]}>
      <View style={styles.contentContainer}>
        <DepositAmount delay={animationDelay.depositAmount} deposit={deposit} />
        {!depositNoRewards && (
          <AdditionalRewards delay={animationDelay.additionalRewards} deposit={deposit} />
        )}
        <RecommendedTemplate delay={animationDelay.recommendedTemplate} onDismiss={handleDismiss} />
        <Animated.View style={[{opacity: fadeAmim2}, styles.closeIconContainer]}>
          <TouchableOpacity activeOpacity={0.8} onPress={handleCrossButton}>
            <SkillzImage source={'promo_code_close_icon.png'} style={styles.newCloseIcon} />
          </TouchableOpacity>
        </Animated.View>
      </View>
    </Animated.View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    // Highest z-index to ensure it's above all SDK content
    zIndex: 99999,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: 'rgba(0, 0, 0, 0.9)',
  },

  contentContainer: {
    flex: 1,
    alignItems: 'center',
    paddingTop: 130,
  },

  checkMarkIcon: {
    width: 50,
    height: 50,
  },

  closeIconContainer: {
    position: 'absolute',
    bottom: 80,
  },

  newCloseIcon: {
    width: 55,
    height: 55,
    alignSelf: 'center',
    marginTop: 50,
  },
});

export default DepositSuccessPopup;
